import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { FormSectionHeader } from '../../../../../shared/form-section-header';
import SharedFormCheckbox from '../../../../../shared/shared-form-checkbox';
import { GroupTemplateFormData } from '../group-template-form-data';
import {
  moderationLevelOptions,
  replyToOptions,
  spamModerationLevelOptions,
} from '../group-template-form-options';
import { TemplateSelectField } from '../template-select-field';

interface SettingsStepProps {
  control: Control<GroupTemplateFormData>;
  errors: FieldErrors<GroupTemplateFormData>;
  setValue: UseFormSetValue<GroupTemplateFormData>;
  watch: UseFormWatch<GroupTemplateFormData>;
}

export default function SettingsStep({
  control,
  errors,
  setValue,
  watch,
}: SettingsStepProps) {
  return (
    <Stack gap={3}>
      <FormSectionHeader>Settings</FormSectionHeader>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="messageModerationLevel"
            control={control}
            label="Moderate Messages"
            options={moderationLevelOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 0.5 }}>
          <SharedFormCheckbox
            control={control}
            name="membersCanPostAsTheGroup"
            label="Allow members to post as the group."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="defaultMessageDenyNotificationText"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                multiline
                rows={3}
                inputProps={{ id: 'defaultMessageDenyNotificationText' }}
                InputLabelProps={{
                  htmlFor: 'defaultMessageDenyNotificationText',
                }}
                label="Rejection Message"
                value={field.value || ''}
                onChange={field.onChange}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1.5 }}>
          <SharedFormCheckbox
            control={control}
            name="sendMessageDenyNotification"
            label="Notify authors when moderators reject their posts."
          />
        </Grid>
      </Grid>
      <Stack gap={1} sx={{ maxWidth: 653 }}>
        <FormSectionHeader>
          <Box component="span" id="spamModerationLevel-heading">
            Spam Messages
          </Box>
        </FormSectionHeader>
        <TemplateSelectField
          name="spamModerationLevel"
          control={control}
          labelledBy="spamModerationLevel-heading"
          options={spamModerationLevelOptions}
        />
      </Stack>
      <Stack gap={1} sx={{ maxWidth: 653 }}>
        <FormSectionHeader>
          <Box component="span" id="replyTo-heading">
            Post Replies
          </Box>
        </FormSectionHeader>
        <TemplateSelectField
          name="replyTo"
          control={control}
          labelledBy="replyTo-heading"
          helperText="Replies to posts can be sent to a custom email."
          options={replyToOptions}
          onChange={(value) => {
            if (value !== 'REPLY_TO_CUSTOM') {
              setValue('customReplyTo', '');
            }
          }}
        />
        {watch('replyTo') === 'REPLY_TO_CUSTOM' && (
          <Controller
            name="customReplyTo"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                inputProps={{ id: 'customReplyTo' }}
                InputLabelProps={{ htmlFor: 'customReplyTo' }}
                label="Custom Address"
                error={!!errors.customReplyTo}
                helperText={errors.customReplyTo?.message || ''}
                value={field.value || ''}
                onChange={field.onChange}
              />
            )}
          />
        )}
      </Stack>
    </Stack>
  );
}
